import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { BridgeRoute, useSitesByBridge } from "@/lib/api/edgelink";
import { AlertCircle, Building2, Server } from "lucide-react";
import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { BridgeSiteGroup } from "./BridgeSiteGroup";

export default function DevicesTab({ bridgeId, routes }: { bridgeId: string; routes: BridgeRoute[] }) {
  const { data: sites, isLoading, isError } = useSitesByBridge(bridgeId, routes);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter sites based on search (matches site name, company name, or device name)
  const filteredSites = useMemo(() => {
    if (!sites) return [];
    if (!searchTerm) return sites;
    const lower = searchTerm.toLowerCase();
    return sites.filter(
      (s) =>
        s.name.toLowerCase().includes(lower) ||
        s.companyName.toLowerCase().includes(lower) ||
        s.devices.some((d) => d.name.toLowerCase().includes(lower))
    );
  }, [sites, searchTerm]);

  // Sort: sites with devices first, then by name
  const sortedSites = useMemo(() => {
    return [...filteredSites].sort((a, b) => {
      // Sites with devices come first
      if (a.deviceCount > 0 && b.deviceCount === 0) return -1;
      if (a.deviceCount === 0 && b.deviceCount > 0) return 1;
      // Then sort by name
      return a.name.localeCompare(b.name);
    });
  }, [filteredSites]);

  const totalDevices = useMemo(() => {
    return sites?.reduce((sum, s) => sum + s.deviceCount, 0) || 0;
  }, [sites]);

  const onlineDevices = useMemo(() => {
    return sites?.reduce((sum, s) => sum + s.devices.filter((d) => d.isOnline).length, 0) || 0;
  }, [sites]);

  if (isLoading) {
    return (
      <div className="space-y-3">
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="rounded-full bg-destructive/10 p-4 mb-4">
          <AlertCircle className="h-8 w-8 text-destructive" />
        </div>
        <h3 className="text-lg font-semibold mb-2">Failed to Load Devices</h3>
        <p className="text-muted-foreground max-w-sm">
          Unable to fetch connected devices. Please try again later.
        </p>
      </div>
    );
  }

  if (!sites || sites.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="rounded-full bg-muted/20 p-4 mb-4">
          <Server className="h-8 w-8 text-muted-foreground" />
        </div>
        <h3 className="text-lg font-semibold mb-2">No Connected Devices</h3>
        <p className="text-muted-foreground max-w-sm mb-4">
          No sites or devices have this EdgeLink bridge set as their route.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">

      {/* Summary & Search */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <Badge variant="outline" className="text-sm py-1 px-3">
            <Server className="h-3.5 w-3.5 mr-1.5" />
            {totalDevices} device{totalDevices !== 1 ? 's' : ''}
          </Badge>
          {onlineDevices > 0 && (
            <Badge variant="default" className="text-sm py-1 px-3 border-emerald-500/30 text-emerald-400">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 mr-1.5" />
              {onlineDevices} online
            </Badge>
          )}
          <Badge variant="default" className="text-sm py-1 px-3">
            <Building2 className="h-3.5 w-3.5 mr-1.5" />
            {sites.length} site{sites.length !== 1 ? 's' : ''}
          </Badge>
        </div>
        <div className="relative">
          <input
            type="text"
            placeholder="Search devices or sites..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="h-9 w-72 rounded-md border border-zinc-700 bg-zinc-900 px-3 py-1 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-lime"
          />
        </div>
      </div>

      {/* Sites with Devices */}
      <div className="space-y-2">
        {sortedSites.map((site) => (
          <BridgeSiteGroup
            key={site.id}
            site={site}
            routes={routes}
            defaultExpanded={sortedSites.length <= 5}
          />
        ))}
      </div>

      {filteredSites.length === 0 && searchTerm && (
        <div className="text-center py-8 text-muted-foreground">
          No devices or sites matching "{searchTerm}"
        </div>
      )}
    </div>
  );
}